import React, { useState, useEffect } from "react";
import { ArrowUpDown, Check, Plus, AlertCircle } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "./ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import { useUser } from "../contexts/UserContext"; // Add this import

const CasinoDirectoryTable = () => {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const [casinos, setCasinos] = useState([]);
  const [editedCasinos, setEditedCasinos] = useState({});
  const [newCasino, setNewCasino] = useState({ name: "", website: "" });
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: "tier",
    direction: "asc",
  });
  const [addingToApps, setAddingToApps] = useState({});
  const [existingApps, setExistingApps] = useState([]);
  const [addError, setAddError] = useState({});
  const { user } = useUser(); // Add user context
  const isAdmin = user?.role === "admin";

  const itemsPerPage = 15;

  const updateClickCounter = async (casinoId) => {
    try {
      const updates = [
        {
          _id: casinoId,
          clickCounter:
            (casinos.find((c) => c._id === casinoId)?.clickCounter || 0) + 1,
        },
      ];

      const response = await fetch(`${API_BASE_URL}directories/updateCasinos`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updates),
      });

      if (response.ok) {
        // Update local state to reflect the incremented counter
        setCasinos((prevCasinos) =>
          prevCasinos.map((casino) =>
            casino._id === casinoId
              ? { ...casino, clickCounter: (casino.clickCounter || 0) + 1 }
              : casino
          )
        );
      }
    } catch (error) {
      console.error("Error updating click counter:", error);
    }
  };

  const handleLinkClick = async (casino, url) => {
    await updateClickCounter(casino._id);
    window.open(url, "_blank");
  };

  const fetchCasinos = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}directories`);
      if (response.ok) {
        const data = await response.json();
        setCasinos(data);
        setEditedCasinos({});
      }
    } catch (error) {
      console.error("Error fetching directories:", error);
    }
  };

  useEffect(() => {
    fetchCasinos();
    const fetchExistingApps = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}apps/${user.id}`);
        if (response.ok) {
          const data = await response.json();
          setExistingApps(data);
        }
      } catch (error) {
        console.error("Error fetching existing apps:", error);
      }
    };

    fetchExistingApps();
  }, []);

  // Function to calculate URL similarity
  const calculateUrlSimilarity = (url1, url2) => {
    // Normalize URLs by removing protocol, www, trailing slashes, and query parameters
    const normalizeUrl = (url) => {
      const steps = {
        original: url,
        lowercase: url.toLowerCase(),
        noProtocol: url.toLowerCase().replace(/^(https?:\/\/)?(www\.)?/, ""),
        noParams: url
          .toLowerCase()
          .replace(/^(https?:\/\/)?(www\.)?/, "")
          .replace(/[?#].*$/, ""),
        noTrailingSlash: url
          .toLowerCase()
          .replace(/^(https?:\/\/)?(www\.)?/, "")
          .replace(/[?#].*$/, "")
          .replace(/\/$/, ""),
        noPath: url
          .toLowerCase()
          .replace(/^(https?:\/\/)?(www\.)?/, "")
          .replace(/[?#].*$/, "")
          .replace(/\/$/, "")
          .replace(/\/.*$/, ""),
        final: url
          .toLowerCase()
          .replace(/^(https?:\/\/)?(www\.)?/, "")
          .replace(/[?#].*$/, "")
          .replace(/\/$/, "")
          .replace(/\/.*$/, "")
          .replace(/[^a-z0-9]/g, ""),
      };

      return steps.final;
    };

    const normalized1 = normalizeUrl(url1);
    const normalized2 = normalizeUrl(url2);

    // Calculate Levenshtein distance
    const matrix = Array(normalized1.length + 1)
      .fill(null)
      .map(() => Array(normalized2.length + 1).fill(null));

    for (let i = 0; i <= normalized1.length; i++) matrix[i][0] = i;
    for (let j = 0; j <= normalized2.length; j++) matrix[0][j] = j;

    for (let i = 1; i <= normalized1.length; i++) {
      for (let j = 1; j <= normalized2.length; j++) {
        const cost = normalized1[i - 1] === normalized2[j - 1] ? 0 : 1;
        matrix[i][j] = Math.min(
          matrix[i - 1][j] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j - 1] + cost
        );
      }
    }

    const maxLength = Math.max(normalized1.length, normalized2.length);
    const similarity =
      1 - matrix[normalized1.length][normalized2.length] / maxLength;

    return similarity;
  };

  const checkForDuplicate = (website) => {
    const SIMILARITY_THRESHOLD = 0.8; // 80% similarity threshold

    return existingApps.find((app) => {
      const similarity = calculateUrlSimilarity(app.website, website);
      return similarity >= SIMILARITY_THRESHOLD;
    });
  };

  const handleAddToApps = async (casino) => {
    setAddError((prev) => ({ ...prev, [casino._id]: null }));

    // Check for duplicates
    const duplicate = checkForDuplicate(casino.website);
    if (duplicate) {
      setAddError((prev) => ({
        ...prev,
        [casino._id]: `Similar to existing casino: ${duplicate.name}`,
      }));
      return;
    }

    setAddingToApps((prev) => ({ ...prev, [casino._id]: true }));

    try {
      await updateClickCounter(casino._id);

      const response = await fetch(`${API_BASE_URL}apps`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userId: user.id,
          name: casino.name,
          website: casino.website,
          currentAmount: 0,
        }),
      });

      if (response.ok) {
        const newApp = await response.json();
        setExistingApps((prev) => [...prev, newApp]);
        setTimeout(() => {
          setAddingToApps((prev) => ({ ...prev, [casino._id]: false }));
        }, 1000);
      } else {
        setAddError((prev) => ({
          ...prev,
          [casino._id]: "Failed to add casino",
        }));
      }
    } catch (error) {
      setAddError((prev) => ({
        ...prev,
        [casino._id]: "Error adding casino",
      }));
    } finally {
      setTimeout(() => {
        setAddingToApps((prev) => ({ ...prev, [casino._id]: false }));
      }, 1000);
    }
  };

  const handleCreateCasino = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_BASE_URL}directories`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(newCasino),
      });

      if (response.ok) {
        setNewCasino({ name: "", website: "" });
        fetchCasinos();
        setCurrentPage(1);
        setSearchTerm("");
      }
    } catch (error) {
      console.error("Error creating casino:", error);
    }
  };

  const handleSaveCasinos = async () => {
    setIsSaving(true);
    setSaveSuccess(false);

    const updates = Object.entries(editedCasinos).map(
      ([casinoId, changes]) => ({
        _id: casinoId,
        ...changes,
      })
    );

    try {
      const response = await fetch(`${API_BASE_URL}directories/updateCasinos`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updates),
      });

      if (response.ok) {
        await fetchCasinos();
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 2000);
      }
    } catch (error) {
      console.error("Error saving casinos:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCellEdit = (casinoId, field, value) => {
    const parsedValue = ["minRedeem", "duration"].includes(field)
      ? parseFloat(value) || 0
      : value;

    setEditedCasinos((prev) => ({
      ...prev,
      [casinoId]: {
        ...(prev[casinoId] || {}),
        [field]: parsedValue,
      },
    }));
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getSortedData = (data) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (aValue === null) return 1;
      if (bValue === null) return -1;

      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  };

  const renderSortIcon = (columnKey) => {
    return (
      <ArrowUpDown
        className={`inline ml-2 h-4 w-4 ${
          sortConfig.key === columnKey ? "opacity-100" : "opacity-50"
        } cursor-pointer`}
      />
    );
  };

  const filteredAndSortedCasinos = React.useMemo(() => {
    const filtered = casinos.filter(
      (casino) =>
        casino.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        casino.website.toLowerCase().includes(searchTerm.toLowerCase())
    );
    return getSortedData(filtered);
  }, [casinos, searchTerm, sortConfig]);

  const paginatedCasinos = React.useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedCasinos.slice(
      startIndex,
      startIndex + itemsPerPage
    );
  }, [filteredAndSortedCasinos, currentPage]);

  const totalPages = Math.ceil(filteredAndSortedCasinos.length / itemsPerPage);

  const openAllWebsites = () => {
    filteredAndSortedCasinos.forEach((casino) => {
      const normalizedUrl =
        casino.website.startsWith("http://") ||
        casino.website.startsWith("https://")
          ? casino.website
          : `https://${casino.website}`;
      window.open(normalizedUrl, "_blank");
    });
  };

  return (
    <Card className="w-full bg-white">
      <CardHeader className="flex flex-col sm:flex-row items-center justify-between space-y-2 sm:space-y-0 pb-4">
        <CardTitle>Directory</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {isAdmin && (
          <form
            onSubmit={handleCreateCasino}
            className="flex flex-col sm:flex-row gap-2"
          >
            <input
              type="text"
              value={newCasino.name}
              onChange={(e) =>
                setNewCasino((prev) => ({ ...prev, name: e.target.value }))
              }
              placeholder="Enter casino name"
              className="flex-grow p-2 border rounded"
            />
            <input
              type="text"
              value={newCasino.website}
              onChange={(e) =>
                setNewCasino((prev) => ({ ...prev, website: e.target.value }))
              }
              placeholder="Enter website URL"
              className="flex-grow p-2 border rounded"
            />
            <button
              type="submit"
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
            >
              Add Casino
            </button>
          </form>
        )}

        <div className="flex flex-col sm:flex-row items-center gap-2">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            placeholder="Search casinos..."
            className="flex-grow p-2 border rounded"
          />
          <button
            onClick={openAllWebsites}
            className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
            disabled={filteredAndSortedCasinos.length === 0}
          >
            Open All Websites
          </button>
          {isAdmin && (
            <button
              onClick={handleSaveCasinos}
              disabled={Object.keys(editedCasinos).length === 0 || isSaving}
              className={`
                px-4 py-2 bg-blue-500 text-white rounded flex items-center gap-2
                ${
                  Object.keys(editedCasinos).length === 0 || isSaving
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-blue-600"
                }
                transition-colors
              `}
            >
              <span>Save Changes</span>
              {isSaving && (
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
              )}
              {saveSuccess && !isSaving && (
                <Check className="h-4 w-4 text-white" />
              )}
            </button>
          )}
        </div>

        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <table className="w-full min-w-[800px]">
            <thead>
              <tr className="bg-gray-50 text-sm">
                <th className="w-12 p-3 text-center font-medium"></th>
                <th
                  className="w-48 p-3 text-left font-medium cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort("name")}
                >
                  Name {renderSortIcon("name")}
                </th>
                <th
                  className="w-40 p-3 text-left font-medium cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort("tier")}
                >
                  Tier {renderSortIcon("tier")}
                </th>
                <th
                  className="w-40 p-3 text-left font-medium cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort("signupBonus")}
                >
                  Signup Bonus {renderSortIcon("signupBonus")}
                </th>
                <th
                  className="w-40 p-3 text-left font-medium cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort("playthrough")}
                >
                  Playthrough {renderSortIcon("playthrough")}
                </th>

                <th className="p-3 text-left font-medium">Notes</th>
              </tr>
            </thead>
            <tbody>
              {paginatedCasinos.map((casino) => {
                const normalizedUrl =
                  casino.website.startsWith("http://") ||
                  casino.website.startsWith("https://")
                    ? casino.website
                    : `https://${casino.website}`;

                return (
                  <tr key={casino._id} className="border-t">
                    <td className="p-3 text-center">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            {existingApps.some(
                              (app) =>
                                calculateUrlSimilarity(
                                  app.website,
                                  casino.website
                                ) >= 0.8
                            ) ? (
                              <span className="inline-flex p-1 rounded-full bg-green-100 text-green-600">
                                <Check className="h-4 w-4" />
                              </span>
                            ) : (
                              <button
                                onClick={() => {
                                  handleAddToApps(casino);
                                  window.open(normalizedUrl, "_blank");
                                }}
                                disabled={addingToApps[casino._id]}
                                className={`
                                  p-1 rounded-full
                                  ${
                                    addError[casino._id]
                                      ? "bg-red-100 text-red-600"
                                      : addingToApps[casino._id]
                                      ? "bg-green-100 text-green-600"
                                      : "bg-blue-100 hover:bg-blue-200 text-blue-600"
                                  }
                                  transition-colors
                                `}
                              >
                                {addError[casino._id] ? (
                                  <AlertCircle className="h-4 w-4" />
                                ) : addingToApps[casino._id] ? (
                                  <Check className="h-4 w-4" />
                                ) : (
                                  <Plus className="h-4 w-4" />
                                )}
                              </button>
                            )}
                          </TooltipTrigger>
                          <TooltipContent
                            style={{ backgroundColor: "#FFFFFF" }}
                          >
                            {existingApps.some(
                              (app) =>
                                calculateUrlSimilarity(
                                  app.website,
                                  casino.website
                                ) >= 0.8
                            )
                              ? "Already in My Casinos"
                              : addError[casino._id]
                              ? addError[casino._id]
                              : "Add this casino to My Casinos"}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </td>
                    <td className="p-3">
                      <button
                        onClick={() => handleLinkClick(casino, normalizedUrl)}
                        className="text-blue-500 hover:underline"
                      >
                        {casino.name}
                      </button>
                    </td>
                    <td className="p-3">
                      {isAdmin ? (
                        <input
                          type="text"
                          value={
                            editedCasinos[casino._id]?.tier !== undefined
                              ? editedCasinos[casino._id].tier
                              : casino.tier || ""
                          }
                          onChange={(e) =>
                            handleCellEdit(casino._id, "tier", e.target.value)
                          }
                          className="w-16 border rounded p-1"
                        />
                      ) : (
                        <span>{casino.tier || ""}</span>
                      )}
                    </td>
                    <td className="p-3">
                      {isAdmin ? (
                        <textarea
                          value={
                            editedCasinos[casino._id]?.signupBonus !== undefined
                              ? editedCasinos[casino._id].signupBonus
                              : casino.signupBonus || ""
                          }
                          onChange={(e) =>
                            handleCellEdit(
                              casino._id,
                              "signupBonus",
                              e.target.value
                            )
                          }
                          className="w-full border rounded p-1 resize-none text-sm"
                          rows={2}
                        />
                      ) : (
                        <span className="text-sm">
                          {casino.signupBonus || ""}
                        </span>
                      )}
                    </td>
                    <td className="p-3">
                      {isAdmin ? (
                        <textarea
                          value={
                            editedCasinos[casino._id]?.playthrough !== undefined
                              ? editedCasinos[casino._id].playthrough
                              : casino.playthrough || ""
                          }
                          onChange={(e) =>
                            handleCellEdit(
                              casino._id,
                              "playthrough",
                              e.target.value
                            )
                          }
                          className="w-full border rounded p-1 resize-none text-sm"
                          rows={2}
                        />
                      ) : (
                        <span className="text-sm">
                          {casino.playthrough || ""}
                        </span>
                      )}
                    </td>
                    <td className="p-3">
                      {isAdmin ? (
                        <textarea
                          value={
                            editedCasinos[casino._id]?.notes !== undefined
                              ? editedCasinos[casino._id].notes
                              : casino.notes || ""
                          }
                          onChange={(e) =>
                            handleCellEdit(casino._id, "notes", e.target.value)
                          }
                          className="w-full border rounded p-1 resize-none text-sm"
                          rows={2}
                        />
                      ) : (
                        <span className="text-sm">{casino.notes || ""}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col sm:flex-row justify-between items-center gap-2">
          <p className="text-sm text-gray-600">
            Total Casinos: {filteredAndSortedCasinos.length}
          </p>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 bg-gray-100 rounded disabled:opacity-50 hover:bg-gray-200 transition-colors"
            >
              Previous
            </button>
            <span className="text-sm text-gray-600">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              className="px-4 py-2 bg-gray-100 rounded disabled:opacity-50 hover:bg-gray-200 transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CasinoDirectoryTable;
